import { useEffect, useMemo, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
  Users,
  Home,
  Send,
  MessageSquare,
  Bell,
  BellRing,
  Paperclip,
  X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Team chat page. Left rail = channels; right pane = messages + composer.
 *
 * Two channel types:
 *   • #team      — one per organization
 *   • #property  — one per property (auto-created on first fetch)
 *
 * Real-time: short-polling every 5 seconds for new messages. Auto-marks
 * the open channel as read.
 */
const POLL_MS = 5000;

const fmtTime = (d) => {
  if (!d) return '';
  try { return format(parseISO(d), "MMM d, h:mm a"); } catch { return d; }
};

export default function TeamChatView() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [pendingPhotos, setPendingPhotos] = useState([]); // [{url, name}]
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
  );
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const requestNotificationPermission = async () => {
    if (typeof Notification === 'undefined') {
      toast.error('Notifications not supported on this device');
      return;
    }
    const result = await Notification.requestPermission();
    setNotifPerm(result);
    if (result === 'granted') {
      toast.success("You'll get a desktop popup when new chats arrive.");
    } else if (result === 'denied') {
      toast.error('Notifications blocked — enable them in your browser settings.');
    }
  };

  // Load channel list + poll to refresh unread counts.
  const loadChannels = async () => {
    try {
      const { data } = await api.get('/team-chat/channels');
      setChannels(data);
      if (!activeId && data.length) {
        setActiveId(data[0].id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadMessages = async (chId) => {
    if (!chId) return;
    try {
      const { data } = await api.get(`/team-chat/channels/${chId}/messages`);
      setMessages(data);
      // Mark this channel as read
      await api.post(`/team-chat/channels/${chId}/read`);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { loadChannels(); }, []);

  useEffect(() => {
    if (!activeId) return;
    loadMessages(activeId);
    const iv = setInterval(() => {
      loadMessages(activeId);
      loadChannels();  // refresh unread badges on other channels
    }, POLL_MS);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, activeId]);

  const activeChannel = useMemo(
    () => channels.find((c) => c.id === activeId),
    [channels, activeId],
  );

  const send = async (e) => {
    e?.preventDefault?.();
    const text = body.trim();
    if ((!text && pendingPhotos.length === 0) || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/team-chat/channels/${activeId}/messages`, {
        body: text,
        photo_urls: pendingPhotos.map((p) => p.url),
      });
      setMessages((prev) => [...prev, data]);
      setBody('');
      setPendingPhotos([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const handlePhotoPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map(async (f) => {
          const fd = new FormData();
          fd.append('file', f);
          const { data } = await api.post('/team-chat/upload-photo', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          return { url: data.url, name: f.name };
        })
      );
      setPendingPhotos((prev) => [...prev, ...uploaded]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="team-chat-view">
      <div>
        <h1 className="font-manrope font-bold text-3xl text-slate-900 tracking-tight">
          Team Chat
        </h1>
        <p className="text-slate-500 mt-1">
          Coordinate with your team. One channel per property + one team-wide channel.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-16rem)] min-h-[420px]">
        {/* ---------- Channel list ---------- */}
        <Card className="p-3 overflow-y-auto" data-testid="chat-channel-list">
          <div className="text-xs uppercase tracking-wider text-slate-400 px-2 mb-2">
            Channels
          </div>
          {channels.length === 0 && (
            <p className="text-sm text-slate-500 px-2 py-4">No channels yet.</p>
          )}
          <ul className="space-y-1">
            {channels.map((c) => {
              const isActive = c.id === activeId;
              const Icon = c.type === 'team' ? Users : Home;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setActiveId(c.id)}
                    data-testid={`chat-channel-${c.id}`}
                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left transition-colors ${
                      isActive
                        ? 'bg-[#0B1E3F] text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-amber-300' : 'text-slate-400'}`} />
                    <span className="flex-1 truncate">{c.name}</span>
                    {c.unread_count > 0 && (
                      <span
                        className="text-[10px] font-bold rounded-full bg-amber-400 text-[#0B1E3F] px-1.5 py-0.5 min-w-[1.25rem] text-center"
                        data-testid={`chat-unread-${c.id}`}
                      >
                        {c.unread_count}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>

        {/* ---------- Messages + composer ---------- */}
        <Card className="flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-slate-400" />
            <h2 className="font-semibold text-slate-900">
              {activeChannel?.name || 'Select a channel'}
            </h2>
            <span className="text-xs text-slate-400 ml-2 capitalize">
              {activeChannel?.type === 'property' ? 'Property channel' : activeChannel?.type}
            </span>
            <div className="ml-auto">
              {notifPerm === 'granted' ? (
                <span
                  className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1"
                  title="Desktop notifications enabled"
                  data-testid="notif-granted"
                >
                  <BellRing className="h-3 w-3" /> Notifications on
                </span>
              ) : notifPerm === 'denied' ? (
                <span
                  className="text-xs text-slate-400"
                  title="Enable in browser site settings"
                >
                  Notifications blocked
                </span>
              ) : notifPerm === 'unsupported' ? null : (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={requestNotificationPermission}
                  data-testid="enable-notifications-btn"
                  className="h-8 text-xs"
                >
                  <Bell className="h-3.5 w-3.5 mr-1.5" />
                  Enable notifications
                </Button>
              )}
            </div>
          </div>

          {/* Message list */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
            data-testid="chat-message-list"
          >
            {messages.length === 0 ? (
              <div className="text-center text-slate-400 text-sm mt-12">
                No messages yet. Say hi 👋
              </div>
            ) : (
              messages.map((m) => {
                const mine = m.user_id === user?.id;
                return (
                  <div
                    key={m.id}
                    className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                    data-testid={`chat-msg-${m.id}`}
                  >
                    <div className={`max-w-[70%] ${mine ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                      {!mine && (
                        <div className="text-xs text-slate-500 px-1">
                          <span className="font-semibold text-slate-700">{m.user_name}</span>
                          {m.user_role && (
                            <span className="ml-1 text-slate-400 capitalize">
                              · {m.user_role.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      )}
                      <div
                        className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                          mine
                            ? 'bg-[#0B1E3F] text-white rounded-br-md'
                            : 'bg-slate-100 text-slate-900 rounded-bl-md'
                        }`}
                      >
                        {(m.photo_urls || []).length > 0 && (
                          <div className="grid grid-cols-2 gap-1 mb-1.5 -mx-1">
                            {m.photo_urls.map((u) => (
                              <a
                                key={u}
                                href={`${process.env.REACT_APP_BACKEND_URL}${u}`}
                                target="_blank"
                                rel="noreferrer"
                                className="block"
                              >
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${u}`}
                                  alt="Attachment"
                                  className="w-full h-32 object-cover rounded-md"
                                />
                              </a>
                            ))}
                          </div>
                        )}
                        {m.body}
                      </div>
                      <div className="text-[10px] text-slate-400 px-1">
                        {fmtTime(m.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={send}
            className="border-t border-slate-100 p-3"
            data-testid="chat-composer"
          >
            {pendingPhotos.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-2" data-testid="chat-pending-photos">
                {pendingPhotos.map((p, i) => (
                  <div key={p.url} className="relative group">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`}
                      alt={p.name}
                      className="h-16 w-16 object-cover rounded-md border border-slate-200"
                    />
                    <button
                      type="button"
                      onClick={() => setPendingPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-red-600"
                      aria-label="Remove photo"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                multiple
                onChange={handlePhotoPick}
                className="hidden"
                data-testid="chat-photo-input"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={!activeChannel || uploading}
                data-testid="chat-attach-btn"
                title="Attach photo"
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Input
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={activeChannel ? `Message #${activeChannel.name}` : 'Select a channel first'}
                disabled={!activeChannel || sending}
                data-testid="chat-composer-input"
                maxLength={2000}
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={
                  (!body.trim() && pendingPhotos.length === 0) ||
                  !activeChannel || sending || uploading
                }
                data-testid="chat-composer-send"
                className="bg-[#0B1E3F] hover:bg-[#0B1E3F]/90 text-white"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            {uploading && (
              <div className="text-xs text-slate-500 mt-1.5">Uploading photo…</div>
            )}
          </form>
        </Card>
      </div>
    </div>
  );
}
